import { Box, Card, Stack, Typography as MuiTypography } from "@mui/material";
import { useTheme, type Theme } from "@mui/material/styles";
import { Button, FoodsIcon, FoodsTabs, SectionTabs, Typography as FoodsTypography } from "@foodpilot/foods";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
    EntityFormPage,
    FormHeader,
    getEntityFormStickyTop,
    SectionCard,
    SectionContent,
    SectionHeader,
    type SectionHeaderProps,
} from "src/components/EntityForm";
import { Breadcrumbs, type BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";
import { URLHandler } from "src/services/URLHandler";
import { useTransportAppearance } from "./transportAppearance";

type TransportCreatePageShellProps = {
    companyId: number;
    title: string;
    saveDisabled: boolean;
    saveLoading?: boolean;
    onSave: () => void | Promise<void>;
    children: ReactNode;
};

type TransportCreateSectionProps = {
    title: string;
    description?: string;
    progress?: SectionHeaderProps["progress"];
    children: ReactNode;
};

type TransportFormMapLayoutProps = {
    children: ReactNode;
};

export type TransportFormTabValue = "form" | "scores";

type TransportFormTabsProps = {
    ariaLabel: string;
    idPrefix: string;
    informationLabel: string;
    scoresLabel: string;
    value: TransportFormTabValue;
    onChange: (value: TransportFormTabValue) => void;
};

type TransportFormTabPanelProps = {
    children: ReactNode;
    idPrefix: string;
    value: TransportFormTabValue;
};

const getTransportFormTabId = (idPrefix: string, value: TransportFormTabValue) => `${idPrefix}-${value}-tab`;
const getTransportFormTabPanelId = (idPrefix: string, value: TransportFormTabValue) => `${idPrefix}-${value}-panel`;

export const TransportCreatePageShell = ({
    companyId,
    title,
    saveDisabled,
    saveLoading = false,
    onSave,
    children,
}: TransportCreatePageShellProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const appearance = useTransportAppearance();
    const breadcrumbs: BreadcrumbType[] = [
        {
            label: t("transport.breadcrumb.all", "All transports"),
            action: () => navigate(URLHandler.transport.list(companyId)),
            disabled: saveLoading,
        },
        {
            label: title,
        },
    ];

    if (appearance === "legacy") {
        const divider = theme.custom.strokes.containerDivider;

        return (
            <Stack data-appearance="legacy" data-testid="transport-create-shell" gap={3}>
                <Box sx={{ mx: { xs: -3, md: -6 }, mt: -3 }}>
                    <Box
                        sx={{
                            backgroundColor: theme.custom.grey[100],
                            borderBottomColor: divider.color,
                            borderBottomStyle: divider.style,
                            borderBottomWidth: divider.width,
                            px: { xs: 3, md: 6 },
                            py: 3,
                        }}
                    >
                        <Breadcrumbs appearance={appearance} items={breadcrumbs} />
                    </Box>

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ md: "center" }}
                        gap={2}
                        sx={{ px: { xs: 3, md: 6 }, py: 3 }}
                    >
                        <MuiTypography component="h1" variant="h2" sx={{ flex: 1 }}>
                            {title}
                        </MuiTypography>
                        <Stack direction="row" gap={1.5}>
                            <Button
                                variant="ternary"
                                startIcon={<FoodsIcon icon="close" size={2} />}
                                disabled={saveLoading}
                                onClick={() => navigate(URLHandler.transport.list(companyId))}
                            >
                                {t("glossary.cancel", "Cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                startIcon={<FoodsIcon icon="check" size={2} />}
                                disabled={saveDisabled || saveLoading}
                                onClick={() => void onSave()}
                            >
                                {t("glossary.save", "Save")}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                {children}
            </Stack>
        );
    }

    return (
        <Box data-appearance="product-v2" data-testid="transport-create-shell">
            <EntityFormPage
                header={(ref) => (
                    <FormHeader
                        ref={ref}
                        breadcrumbs={breadcrumbs}
                        title={title}
                        onCancel={() => navigate(URLHandler.transport.list(companyId))}
                        onSave={() => void onSave()}
                        cancelDisabled={saveLoading}
                        saving={saveLoading}
                        saveDisabled={saveDisabled}
                        testIdPrefix="transport"
                    />
                )}
            >
                {children}
            </EntityFormPage>
        </Box>
    );
};

export const TransportCreateSection = ({ title, description, progress, children }: TransportCreateSectionProps) => {
    const theme = useTheme();
    const appearance = useTransportAppearance();

    if (appearance === "legacy") {
        const legacyTitle = progress ? `${progress.current}/${progress.total} • ${title}` : title;

        return (
            <Card
                variant="outlined"
                sx={{
                    borderRadius: theme.spacing(1.5),
                    borderColor: theme.custom.grey[500],
                    backgroundColor: theme.custom.grey[100],
                    boxShadow: theme.custom.shadows.grey,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        backgroundColor: theme.custom.grey[300],
                        borderBottomColor: theme.custom.grey[500],
                        borderBottomStyle: "solid",
                        borderBottomWidth: 1,
                    }}
                >
                    <Stack gap={0.5}>
                        <MuiTypography component="h2" variant="body1" sx={{ fontWeight: 600 }}>
                            {legacyTitle}
                        </MuiTypography>
                        {description && (
                            <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                {description}
                            </MuiTypography>
                        )}
                    </Stack>
                </Box>
                <Stack gap={2} sx={{ p: 2 }}>
                    {children}
                </Stack>
            </Card>
        );
    }

    return (
        <SectionCard>
            <SectionHeader title={title} subtitle={description} progress={progress} />
            <SectionContent>{children}</SectionContent>
        </SectionCard>
    );
};

export const TransportFormTabs = ({
    ariaLabel,
    idPrefix,
    informationLabel,
    scoresLabel,
    value,
    onChange,
}: TransportFormTabsProps) => {
    const theme = useTheme();
    const appearance = useTransportAppearance();
    const divider = theme.custom.strokes.containerDivider;

    if (appearance === "legacy") {
        return (
            <SectionTabs
                tabs={[
                    {
                        name: informationLabel,
                        icon: <FoodsIcon size={2} icon="informations" />,
                        onClick: () => onChange("form"),
                    },
                    {
                        name: scoresLabel,
                        icon: <FoodsIcon size={2} icon="scoresIndicators" />,
                        onClick: () => onChange("scores"),
                    },
                ]}
                value={value === "form" ? 0 : 1}
                renderComponents={false}
                sx={{ alignSelf: "flex-start" }}
            />
        );
    }

    return (
        <Box
            sx={{
                "& .MuiTab-root": {
                    borderRadius: 0,
                },
                "& .MuiTab-root.Mui-selected, & .MuiTab-root.Mui-selected:hover, & .MuiTab-root.Mui-selected:focus-visible":
                    {
                        background: "transparent",
                    },
                borderBottomColor: divider.color,
                borderBottomStyle: divider.style,
                borderBottomWidth: divider.width,
                width: "100%",
            }}
        >
            <FoodsTabs
                BoxProps={{ sx: { maxWidth: "100%", width: "max-content" } }}
                forceScrollable
                selectedTabIndex={value === "form" ? 0 : 1}
                tabs={[
                    {
                        id: "form",
                        icon: "informations",
                        name: informationLabel,
                        TabProps: {
                            "aria-controls": getTransportFormTabPanelId(idPrefix, "form"),
                            id: getTransportFormTabId(idPrefix, "form"),
                        },
                    },
                    {
                        id: "scores",
                        icon: "scoresIndicators",
                        name: scoresLabel,
                        TabProps: {
                            "aria-controls": getTransportFormTabPanelId(idPrefix, "scores"),
                            id: getTransportFormTabId(idPrefix, "scores"),
                        },
                    },
                ]}
                tabVariant="primary"
                TabProps={{
                    sx: {
                        gap: theme.spacing(1),
                    },
                }}
                TabsProps={{
                    "aria-label": ariaLabel,
                    onChange: (_event, nextIndex) => onChange(nextIndex === 0 ? "form" : "scores"),
                }}
            />
        </Box>
    );
};

export const TransportFormTabPanel = ({ children, idPrefix, value }: TransportFormTabPanelProps) => {
    const appearance = useTransportAppearance();

    if (appearance === "legacy") {
        return children;
    }

    return (
        <Box
            aria-labelledby={getTransportFormTabId(idPrefix, value)}
            id={getTransportFormTabPanelId(idPrefix, value)}
            role="tabpanel"
        >
            {children}
        </Box>
    );
};

export const TransportFormMapLayout = ({ children }: TransportFormMapLayoutProps) => {
    const theme = useTheme();
    const appearance = useTransportAppearance();

    return (
        <Box
            data-testid="transport-form-map-layout"
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    ...(appearance === "legacy" ?
                        { xl: "minmax(460px, 1fr) minmax(420px, 0.95fr)" }
                    :   { lg: `minmax(0, 2fr) minmax(${theme.spacing(37.5)}, 1fr)` }),
                },
                gap: 3,
                alignItems: "start",
            }}
        >
            {children}
        </Box>
    );
};

export const TransportFormMapPanel = ({ children, title }: { children: ReactNode; title: string }) => {
    const theme = useTheme();
    const appearance = useTransportAppearance();

    if (appearance === "legacy") {
        return (
            <Card
                data-testid="transport-form-map-panel"
                variant="outlined"
                sx={{
                    borderRadius: theme.spacing(1.5),
                    borderColor: theme.custom.grey[500],
                    backgroundColor: theme.custom.grey[100],
                    boxShadow: theme.custom.shadows.grey,
                    p: 2,
                    position: { xl: "sticky" },
                    top: { xl: theme.spacing(3) },
                }}
            >
                <Stack gap={2}>
                    <MuiTypography component="h2" variant="h3" sx={{ fontSize: theme.spacing(2) }}>
                        {title}
                    </MuiTypography>
                    {children}
                </Stack>
            </Card>
        );
    }

    return (
        <SectionCard
            data-testid="transport-form-map-panel"
            sx={{
                position: { lg: "sticky" },
                top: { lg: getEntityFormStickyTop(theme.spacing(3)) },
            }}
        >
            <Stack gap={2} padding={2}>
                <FoodsTypography component="h2" variant="big-bold">
                    {title}
                </FoodsTypography>
                {children}
            </Stack>
        </SectionCard>
    );
};

export const transportCreateTextFieldSx = (theme: Theme) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: theme.spacing(1),
        backgroundColor: theme.custom.grey[100],
        "& fieldset": {
            borderColor: theme.custom.grey[500],
        },
        "&:hover": {
            backgroundColor: theme.custom.grey[300],
        },
        "&.Mui-focused fieldset": {
            borderColor: theme.custom.grey[2800],
            borderWidth: 2,
        },
        "&.Mui-disabled": {
            backgroundColor: theme.custom.grey[500],
        },
    },
    "& .MuiInputLabel-root": {
        color: theme.custom.grey[1400],
    },
    "& .MuiFormHelperText-root": {
        color: theme.custom.grey[2000],
    },
});
